import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatedIcon } from '../../components/icons';
import { useDealStore } from '../../store/dealStore';
import { useAuthStore } from '../../store/authStore';
import { RequestState, Deal, SurveyorProfile, SURVEY_CATEGORIES } from '../../types';
import { PieChart, PIE_COLORS } from '../../components/PieChart';
import { WEB_STATUS_COLORS } from '../../utils/statusPresentation';

const STATUS_COLORS = WEB_STATUS_COLORS;
const COMMISSION_PAYMENT_NOTICE =
  'Pay commission via EcoCash or InnBucks on 0784 454 242. After payment, upload proof of payment and wait for admin approval.';

const getCompactStatusText = (status: string) => {
  switch (status) {
    case 'UNDER_REVIEW': return '🟡 Review';
    case 'ASSIGNED': return '🔵 Assigned';
    case 'NEGOTIATING': return '🟡 Negotiating';
    case 'CONFIRMED': return '🟢 Confirmed';
    case 'CLOSED': return '🟢 Closed';
    case 'REQUEST_SUBMITTED': return '🔵 Submitted';
    case 'APPROVED': return '🟢 Approved';
    case 'DISPUTED': return '🟠 Disputed';
    case 'REJECTED': return '🔴 Rejected';
    default: return status.replace(/_/g, ' ');
  }
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const buildMonthlyEarnings = (deals: Deal[]) => {
  const now = new Date();
  const months: { label: string; revenue: number; commission: number }[] = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const label = MONTH_NAMES[d.getMonth()];
    const monthDeals = deals.filter((dl) => {
      const dd = new Date(dl.created_at);
      return dd.getMonth() === d.getMonth() && dd.getFullYear() === d.getFullYear();
    });
    const revenue = monthDeals.reduce((s, dl) => s + (dl.deal_value || 0), 0);
    const commission = monthDeals.reduce((s, dl) => s + (dl.expected_commission || 0), 0);
    months.push({ label, revenue, commission });
  }
  return months;
};

const CSS = `
.zs-stat-card {
  position: relative;
  overflow: hidden;
  height: 100%;
  border-radius: var(--sc-radius-lg);
  border: 1px solid var(--sc-border);
  background: var(--sc-bg-secondary);
  box-shadow: var(--sc-shadow-sm);
  padding: 10px 14px;
  text-decoration: none;
  display: block;
  transition: all var(--sc-transition-normal);
}
.zs-stat-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--sc-shadow-md);
}
.zs-stat-card-circle {
  position: absolute;
  top: -16px;
  right: -16px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}
.zs-stat-card:hover .zs-stat-card-circle {
  transform: scale(1.15);
}
.zs-stat-card-iconbox {
  width: 32px;
  height: 32px;
  border-radius: var(--sc-radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.zs-stat-card:hover .zs-stat-card-iconbox {
  transform: scale(1.06) rotate(-3deg);
}
`;

export const SurveyorDashboard: React.FC = () => {
  const { user } = useAuthStore();
  const profile = user as SurveyorProfile | null;
  const myServiceTypes: string[] = profile?.service_types ?? [];
  const { deals: allDeals, isLoading, fetchDeals } = useDealStore();

  const [hoveredBar, setHoveredBar] = useState<number | null>(null);
  const [hoveredFunnel, setHoveredFunnel] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => { fetchDeals(); }, []);

  // The API/store already scopes deals to the signed-in surveyor. Do not filter
  // by service metadata here, because spelling/category drift can hide real jobs.
  const deals = allDeals;

  const countFor = (status: RequestState) => deals.filter((d) => d.status === status).length;
  const formatDate = (d: string) => new Date(d).toLocaleDateString('en-ZW', { day: 'numeric', month: 'short' });

  /* ─── Computed Analytics ──────────────────────────────── */
  const analytics = useMemo(() => {
    const totalRevenue = deals.reduce((s, d) => s + (d.deal_value || 0), 0);
    const totalCommission = deals.reduce((s, d) => s + (d.expected_commission || 0), 0);
    const netEarnings = totalRevenue - totalCommission;
    const confirmedDeals = deals.filter((d) => d.status === RequestState.CONFIRMED || d.status === RequestState.CLOSED);
    const completionRate = deals.length > 0 ? Math.round((confirmedDeals.length / deals.length) * 100) : 0;
    const avgDealValue = confirmedDeals.length > 0 ? totalRevenue / confirmedDeals.length : 0;

    // Service type breakdown
    const typeMap: Record<string, { count: number; value: number }> = {};
    deals.forEach((d) => {
      const t = d.request?.service_type ?? 'Unknown';
      if (!typeMap[t]) typeMap[t] = { count: 0, value: 0 };
      typeMap[t].count++;
      typeMap[t].value += d.deal_value || 0;
    });
    const serviceBreakdown = Object.entries(typeMap)
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 6)
      .map(([type, data]) => ({ type, ...data, pct: deals.length > 0 ? Math.round((data.count / deals.length) * 100) : 0 }));

    // Pipeline funnel
    const pipeline = [
      { label: 'Assigned', count: countFor(RequestState.ASSIGNED), color: '#7C3AED' },
      { label: 'Negotiating', count: countFor(RequestState.NEGOTIATING), color: '#EA580C' },
      { label: 'Confirmed', count: countFor(RequestState.CONFIRMED), color: '#10B981' },
      { label: 'Closed', count: countFor(RequestState.CLOSED), color: '#64748B' },
    ];
    const maxPipeline = Math.max(...pipeline.map((p) => p.count), 1);

    // Monthly earnings
    const monthlyEarnings = buildMonthlyEarnings(deals);
    const maxMonthRevenue = Math.max(...monthlyEarnings.map((m) => m.revenue), 1);

    // Avg response time
    const avgResponseDays = deals.length > 0
      ? deals.reduce((s, d) => {
          const days = (new Date(d.updated_at).getTime() - new Date(d.created_at).getTime()) / (1000 * 60 * 60 * 24);
          return s + days;
        }, 0) / deals.length
      : 0;

    return { totalRevenue, totalCommission, netEarnings, completionRate, avgDealValue, serviceBreakdown, pipeline, maxPipeline, monthlyEarnings, maxMonthRevenue, avgResponseDays };
  }, [deals]);

  // ── Stat cards (matched to history page) ──────────────
  const totalJobs = deals.length;
  const activeJobs = deals.filter((d) => ['ASSIGNED', 'NEGOTIATING'].includes(d.status)).length;
  const confirmedJobs = deals.filter((d) => d.status === 'CONFIRMED').length;
  const closedJobs = deals.filter((d) => d.status === 'CLOSED').length;
  const grossVolume = deals.reduce((sum, d) => sum + (d.deal_value || 0), 0);
  const commissionDue = deals
    .filter((d) => d.payment_status !== 'PAID' && d.status !== 'CLOSED')
    .reduce((sum, d) => sum + (d.expected_commission || 0), 0);
  const totalEarnings = deals
    .filter((d) => d.status === 'CONFIRMED' || d.status === 'CLOSED')
    .reduce((sum, d) => sum + (d.deal_value || 0) - (d.expected_commission || 0), 0);

  // ── Suspension gate ──────────────────────────────────────
  if (profile?.is_suspended) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column' as const, alignItems: 'center', justifyContent: 'center', minHeight: '60vh', padding: 40, textAlign: 'center' as const }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>Account Locked</div>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: '#DC2626', margin: '0 0 10px' }}>Account Suspended</h2>
        <p style={{ fontSize: 14, color: '#636E72', maxWidth: 440, lineHeight: 1.6, margin: '0 0 16px' }}>
          Your account has been suspended. You cannot access new jobs or active negotiations until your suspension is lifted by an admin.
        </p>
        {profile.suspension_reason && (
          <div style={{ background: '#FEE2E2', borderRadius: 10, padding: '12px 20px', fontSize: 13, color: '#B91C1C', fontStyle: 'italic', maxWidth: 400, marginBottom: 16 }}>
            Reason: {profile.suspension_reason}
          </div>
        )}
        <div style={{ background: 'var(--sc-warning-bg)', border: '1px solid var(--sc-warning)', borderRadius: 10, padding: '12px 20px', fontSize: 13, color: 'var(--sc-warning-text)', maxWidth: 440, fontWeight: 700, lineHeight: 1.5 }}>
          {COMMISSION_PAYMENT_NOTICE}
        </div>
      </div>
    );
  }

  if (isMobile) {
    const recentDeals = deals.slice(0, 5);

    return (
      <div style={s.mobileContainer}>
        {/* Top Dark Navy unified card section */}
        <div style={s.mobileHero}>
          <span style={s.paypalCardLabel}>Net Earn</span>
          <h2 style={s.paypalCardBal}>${totalEarnings.toLocaleString('en-US', { minimumFractionDigits: 2 })} <span style={{ fontSize: 16, fontWeight: 500, color: '#94A3B8' }}>USD</span></h2>
          <span style={s.paypalCardSub}>Funds secured in active deals</span>
          <Link to="/surveyor/profile" style={s.paypalCardBtn}>Withdraw Funds</Link>
        </div>

        {/* Sliding body sheet */}
        <div style={s.mobileSheet}>
          
          {/* Quick Actions circular buttons row */}
          <div style={s.actionGridMobile}>
            <Link to="/surveyor/browse-jobs" style={s.actionItemMobile}>
              <div style={s.actionCircleMobile}>
                <span style={{ fontSize: 20 }}>🔭</span>
              </div>
              <span style={s.actionLabelMobile}>Browse</span>
            </Link>
            <Link to="/surveyor/advertise-jobs" style={s.actionItemMobile}>
              <div style={s.actionCircleMobile}>
                <span style={{ fontSize: 20 }}>📣</span>
              </div>
              <span style={s.actionLabelMobile}>Advertise</span>
            </Link>
            <Link to="/surveyor/history" style={s.actionItemMobile}>
              <div style={s.actionCircleMobile}>
                <span style={{ fontSize: 20 }}>📊</span>
              </div>
              <span style={s.actionLabelMobile}>History</span>
            </Link>
            <Link to="/surveyor/messages" style={s.actionItemMobile}>
              <div style={s.actionCircleMobile}>
                <span style={{ fontSize: 20 }}>💬</span>
              </div>
              <span style={s.actionLabelMobile}>Inbox</span>
            </Link>
          </div>

          {/* Divider */}
          <div style={s.sectionDivider} />

          {/* Active Clients */}
          <div style={{ marginBottom: 24 }}>
            <h3 style={s.mobileSecTitle}>Active Clients</h3>
            <div style={s.avatarScroll}>
              {[
                { name: 'Survey Connect', initial: 'S', color: '#0C5ADB' },
                { name: 'John Doe', initial: 'J', color: '#BAE6FD' },
                { name: 'Jane Smith', initial: 'J', color: '#DDD6FE' },
                { name: 'Bob Johnson', initial: 'B', color: '#BBF7D0' },
                { name: 'Alice Brown', initial: 'A', color: '#FBCFE8' },
              ].map((contact, i) => (
                <div key={i} style={s.avatarCol} onClick={() => navigate('/surveyor/messages')}>
                  <div style={{ ...s.avatarCircle, backgroundColor: contact.color }}>
                    <span style={{ ...s.avatarCircleText, color: contact.color === '#0C5ADB' ? '#FFF' : '#0A1C30' }}>{contact.initial}</span>
                  </div>
                  <span style={s.avatarName}>{contact.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ ...s.mobileSecTitle, margin: 0 }}>Recent activity</h3>
              <Link to="/surveyor/history" style={{ fontSize: 13, fontWeight: 700, color: '#0C5ADB', textDecoration: 'none' }}>Show all</Link>
            </div>
            
            <div style={s.activityList}>
              {recentDeals.map((deal) => (
                <Link to={`/surveyor/deal/${deal.id}`} key={deal.id} style={s.activityItem} className="zs-card-hover">
                  <div style={s.activityIcon}>
                    <span style={{ fontSize: 16 }}>📐</span>
                  </div>
                  <div style={s.activityInfo}>
                    <span style={s.activityTitle}>{deal.request?.service_type || 'Job Request'}</span>
                    <span style={s.activityDate}>
                      {new Date(deal.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} • <span style={{ fontWeight: 600 }}>{getCompactStatusText(deal.status)}</span>
                    </span>
                  </div>
                  <div style={{ ...s.activityValue, color: '#229A39' }}>
                    +${((deal.deal_value || 0) - (deal.expected_commission || 0)).toFixed(2)}
                  </div>
                </Link>
              ))}
              {recentDeals.length === 0 && (
                <div style={{ textAlign: 'center', padding: 20, color: '#636E72', fontSize: 14 }}>
                  No active jobs or activity.
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={s.header}>
        <div style={{ flex: 1 }}>
          <h1 style={s.pageTitle}>Surveyor Dashboard</h1>
          <p style={s.pageSubtitle}>Welcome back, {user?.first_name}. Track your performance and earnings.</p>
          {/* Specialties strip */}
          {myServiceTypes.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
              {myServiceTypes.map((st) => {
                const cat = SURVEY_CATEGORIES.find((c) => c.services.includes(st));
                return (
                  <span key={st} style={{
                    padding: '3px 10px',
                    borderRadius: 8,
                    fontSize: 11,
                    fontWeight: 600,
                    background: (cat?.color ?? '#10B981') + '18',
                    color: cat?.color ?? '#10B981',
                    border: `1px solid ${(cat?.color ?? '#10B981')}33`,
                  }}>{cat?.icon} {st}</span>
                );
              })}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Link to="/surveyor/browse-jobs" style={{ ...s.profileBtn, background: '#10B981', color: '#FFF', border: 'none' }}>🔭 Browse Jobs</Link>
          <Link to="/surveyor/profile" style={s.profileBtn}>👤 My Profile</Link>
        </div>
      </div>

      {/* ── Quick Access KPI Row ── */}
      <div style={s.kpiRow}>
        <style>{CSS}</style>
        {[
          { label: 'Total Jobs', val: totalJobs.toString(), icon: 'clipboard', color: '#0C5ADB' },
          { label: 'Active', val: activeJobs.toString(), icon: 'tools', color: '#FDCB6E' },
          { label: 'Confirmed', val: confirmedJobs.toString(), icon: 'success', color: '#10B981' },
          { label: 'Closed', val: closedJobs.toString(), icon: 'target', color: '#64748B' },
          { label: 'Gross Vol', val: `$${grossVolume.toFixed(0)}`, icon: 'chart', color: '#0EA5E9' },
          { label: 'Comm. Due', val: `$${commissionDue.toFixed(0)}`, icon: 'money', color: '#EA580C' },
          { label: 'Net Earn', val: `$${totalEarnings.toFixed(2)}`, icon: 'money', color: '#10B981' },
        ].map((k) => (
          <div key={k.label} className="zs-stat-card">
            {/* Top color bar */}
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, height: 3,
              background: `linear-gradient(90deg, ${k.color}, ${k.color}88)`
            }} />
            {/* Decorative circle */}
            <div className="zs-stat-card-circle" style={{ backgroundColor: `${k.color}0A` }} />
            
            {/* Content inner */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 1, gap: 4, textAlign: 'center' }}>
              <div className="zs-stat-card-iconbox" style={{ background: `linear-gradient(135deg, ${k.color}15 0%, ${k.color}08 100%)`, color: k.color, border: `1px solid ${k.color}12`, margin: '0 auto' }}>
                <AnimatedIcon name={k.icon as any} size={16} color={k.color} />
              </div>
              <div style={{ fontSize: 9, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 1 }}>
                {k.label}
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.02em', lineHeight: 1 }}>
                {k.val}
              </div>
            </div>
          </div>
        ))}
      </div>

      {commissionDue > 0 && (
        <div style={s.paymentNotice}>
          <AnimatedIcon name="money" size={16} color="var(--sc-warning-text)" />
          <span>{COMMISSION_PAYMENT_NOTICE}</span>
        </div>
      )}

      {/* ── Analytics row 2 — large earnings chart (3fr) + pipeline funnel (2fr) ── */}
      <div style={s.analyticsRow2}>
        {/* Monthly Earnings Chart on dark card */}
        <div style={s.trendCard}>
          <h3 style={s.trendTitle}><AnimatedIcon name="money" size={18} color="#B2BEC3" /> Earnings Trend (6 Months)</h3>
          <div style={s.trendSubtitle}>Total revenue ${analytics.totalRevenue.toFixed(0)}</div>
          <div style={s.barChart}>
            {analytics.monthlyEarnings.map((m, i) => {
              const BAR_COLORS = ['#A5B4FC','#FCA5A5','#6EE7B7','#FDE68A','#C4B5FD','#93C5FD'];
              const color = BAR_COLORS[i % BAR_COLORS.length];
              const isHov = hoveredBar === i;
              const pct = analytics.maxMonthRevenue > 0 ? (m.revenue / analytics.maxMonthRevenue) * 100 : 0;
              return (
                <div key={i} style={s.barCol}
                  onMouseEnter={() => setHoveredBar(i)}
                  onMouseLeave={() => setHoveredBar(null)}>
                  <div style={{ ...s.barValue, color, opacity: hoveredBar !== null && !isHov ? 0.3 : 1, transform: isHov ? 'scale(1.18)' : 'scale(1)', transition: 'transform 0.15s,opacity 0.2s' }}>
                    ${m.revenue.toFixed(0)}
                  </div>
                  <div style={s.barTrack}>
                    <div style={{ ...s.barFill, height: `${pct}%`, background: `linear-gradient(180deg,${color}55 0%,${color} 100%)`, opacity: hoveredBar !== null && !isHov ? 0.22 : 1, boxShadow: isHov ? `0 -8px 22px ${color}77` : 'none', transform: isHov ? 'scaleX(1.07)' : 'scaleX(1)', transition: 'height 0.55s cubic-bezier(.34,1.56,.64,1),box-shadow 0.2s,opacity 0.2s,transform 0.15s' }} />
                  </div>
                  <div style={{ ...s.barLabel, color: isHov ? color : '#636E72', fontWeight: isHov ? 700 : 400, transition: 'color 0.15s' }}>{m.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Deal Pipeline Funnel */}
        <div style={s.analyticCard}>
          <h3 style={s.analyticTitle}><AnimatedIcon name="chart" size={18} color="#636E72" /> Deal Pipeline</h3>
          <div style={s.funnelList}>
            {analytics.pipeline.map((p, i) => {
              const widthPct = analytics.maxPipeline > 0 ? (p.count / analytics.maxPipeline) * 100 : 0;
              const isHov = hoveredFunnel === i;
              return (
                <div key={p.label} style={s.funnelRow} onMouseEnter={() => setHoveredFunnel(i)} onMouseLeave={() => setHoveredFunnel(null)}>
                  <div style={{ ...s.funnelLabel, color: isHov ? p.color : '#2D3436', transition: 'color 0.15s' }}>{p.label}</div>
                  <div style={s.funnelBarTrack}>
                    <div style={{ ...s.funnelBarFill, width: `${widthPct}%`, background: `linear-gradient(90deg,${p.color}BB 0%,${p.color} 100%)`, boxShadow: isHov ? `inset 0 1px 0 rgba(255,255,255,0.3),0 0 12px ${p.color}55` : 'none', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 7, transition: 'width 0.55s cubic-bezier(.34,1.56,.64,1),box-shadow 0.2s' }}>
                      {widthPct > 22 && <span style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.92)', pointerEvents: 'none' }}>{Math.round(widthPct)}%</span>}
                    </div>
                  </div>
                  <div style={{ ...s.funnelCount, color: isHov ? p.color : '#2D3436', transition: 'color 0.15s' }}>{p.count}</div>
                </div>
              );
            })}
          </div>
          <div style={s.funnelSummary}>
            <div style={s.funnelStat}><span style={s.funnelStatNum}>{deals.length}</span><span style={s.funnelStatLabel}>Total Deals</span></div>
            <div style={s.funnelStat}><span style={{ ...s.funnelStatNum, color: '#10B981' }}>{analytics.completionRate}%</span><span style={s.funnelStatLabel}>Win Rate</span></div>
          </div>
        </div>
      </div>

      {/* ── Analytics row 3 — combined Services + Performance card ── */}
      <div style={s.analyticCard}>
        <div style={s.combinedCardInner}>
          {/* Left: Service Type Pie */}
          <div style={s.combinedLeft}>
            <h3 style={s.analyticTitle}><AnimatedIcon name="folder" size={18} color="#636E72" /> Services by Type</h3>
            {analytics.serviceBreakdown.length > 0 ? (
              <PieChart
                data={analytics.serviceBreakdown.map((sb, i) => ({ label: sb.type, value: sb.count, color: PIE_COLORS[i % PIE_COLORS.length] }))}
                size={110} ringWidth={34} centerSub="services"
              />
            ) : <p style={{ color: '#636E72', fontSize: 13 }}>No deals data yet</p>}
          </div>

          {/* Vertical divider */}
          <div style={s.combinedDivider} />

          {/* Right: Performance Metrics */}
          <div style={s.combinedRight}>
            <h3 style={s.analyticTitle}><AnimatedIcon name="target" size={18} color="#636E72" /> Performance Metrics</h3>
            <div style={s.metricsGrid}>
              <div style={s.metricItem}>
                <div style={s.metricCircle}>
                  <svg width="72" height="72" viewBox="0 0 72 72">
                    <circle cx="36" cy="36" r="30" fill="none" stroke="#E8ECF0" strokeWidth="6" />
                    <circle cx="36" cy="36" r="30" fill="none" stroke="#6366F1" strokeWidth="6"
                      strokeDasharray={`${(analytics.completionRate / 100) * 188.5} 188.5`}
                      strokeLinecap="round" transform="rotate(-90 36 36)" />
                  </svg>
                  <span style={s.metricCircleText}>{analytics.completionRate}%</span>
                </div>
                <div style={s.metricLabel}>Win Rate</div>
              </div>
              <div style={s.metricItem}>
                <div style={{ ...s.metricBigNum, color: '#4FACFE' }}>{analytics.avgResponseDays.toFixed(1)}</div>
                <div style={s.metricLabel}>Avg. Days</div>
              </div>
              <div style={s.metricItem}>
                <div style={{ ...s.metricBigNum, color: '#43E97B' }}>${analytics.avgDealValue.toFixed(0)}</div>
                <div style={s.metricLabel}>Avg. Deal Value</div>
              </div>
              <div style={s.metricItem}>
                <div style={{ ...s.metricBigNum, color: '#A18CD1' }}>${analytics.netEarnings.toFixed(0)}</div>
                <div style={s.metricLabel}>Net Earnings</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Deals Table ────────────────────────────────────── */}
      <h2 style={{ fontSize: 18, fontWeight: 700, color: '#2D3436', margin: '28px 0 16px' }}>Active Deals</h2>
      {isLoading && deals.length === 0 ? (
        <div style={s.emptyBox}><div style={s.spinner} /><p>Loading deals...</p></div>
      ) : deals.length === 0 ? (
        <div style={s.emptyBox}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
          <h3 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 8px' }}>No deals yet</h3>
          <p style={{ fontSize: 14, color: '#636E72' }}>Deals will appear here once admin assigns you to requests.</p>
        </div>
      ) : (
        <div style={s.table}>
          <div style={s.tableHead}>
            <div style={{ ...s.th, flex: 2 }}>Service</div>
            <div style={{ ...s.th, flex: 2 }}>Client</div>
            <div style={s.th}>Status</div>
            <div style={s.th}>Value</div>
            <div style={s.th}>Commission</div>
            <div style={s.th}>Date</div>
            <div style={{ ...s.th, flex: 0.5 }}></div>
          </div>
          {deals.map((deal) => {
            const sc = STATUS_COLORS[deal.status] || STATUS_COLORS.CLOSED;
            return (
              <Link to={`/surveyor/deal/${deal.id}`} key={deal.id} style={s.tableRow}>
                <div style={{ ...s.td, flex: 2, fontWeight: 600 }}>{deal.request?.service_type}</div>
                <div style={{ ...s.td, flex: 2, color: '#636E72' }}>{deal.client?.first_name} {deal.client?.last_name}</div>
                <div style={s.td}><span style={{ ...s.badge, background: sc.bg, color: sc.color, borderColor: sc.border }}>{getCompactStatusText(deal.status)}</span></div>
                <div style={{ ...s.td, fontWeight: 600 }}>{deal.deal_value ? `$${deal.deal_value.toFixed(2)}` : '—'}</div>
                <div style={{ ...s.td, color: '#EA580C', fontWeight: 600 }}>{deal.expected_commission ? `-$${deal.expected_commission.toFixed(2)}` : '—'}</div>
                <div style={{ ...s.td, color: '#636E72', fontSize: 12 }}>{formatDate(deal.created_at)}</div>
                <div style={{ ...s.td, flex: 0.5, textAlign: 'center' }}>→</div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  pageTitle: { fontSize: 28, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 4px', letterSpacing: '-0.5px', fontFamily: "'Outfit', sans-serif" },
  pageSubtitle: { fontSize: 14, color: 'var(--sc-text-secondary)', margin: 0, fontFamily: "'Inter', sans-serif" },
  profileBtn: { display: 'inline-flex', padding: '10px 18px', background: 'var(--sc-bg-secondary)', border: '1px solid var(--sc-border)', borderRadius: 10, fontSize: 13, fontWeight: 600, color: 'var(--sc-text-secondary)', textDecoration: 'none', boxShadow: 'var(--sc-shadow-xs)', transition: 'all 0.2s' },

  kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 10, marginBottom: 24 },
  paymentNotice: { margin: '-8px 0 24px', display: 'flex', alignItems: 'flex-start', gap: 10, background: 'var(--sc-warning-bg)', border: '1px solid var(--sc-warning)', color: 'var(--sc-warning-text)', borderRadius: 12, padding: '14px 16px', fontSize: 13, fontWeight: 700, lineHeight: 1.45 },
  kpiCard: { flex: 1, background: 'var(--sc-bg-secondary)', borderRadius: 16, padding: '20px 22px', border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-sm)', textAlign: 'center' as const },
  kpiIcon: { width: 44, height: 44, borderRadius: '50%', background: 'var(--sc-bg-primary)', border: '1px solid var(--sc-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' },
  kpiVal: { fontSize: 24, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '6px 0 2px' },
  kpiLabel: { fontSize: 11, color: 'var(--sc-text-secondary)', fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.5px' },

  /* ─── Analytics ─────────────────────────────────────── */
  analyticsRow2: { display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 16, marginBottom: 16 },
  analyticsRow3: { display: 'none' },
  combinedCardInner: { display: 'flex', gap: 0, alignItems: 'flex-start' },
  combinedLeft: { flex: '0 0 auto', minWidth: 220, paddingRight: 24 },
  combinedDivider: { width: 1, alignSelf: 'stretch', background: 'var(--sc-border)', margin: '0 24px 0 0', flexShrink: 0 },
  combinedRight: { flex: 1, minWidth: 0 },
  trendCard: { background: 'var(--sc-bg-secondary)', borderRadius: 16, padding: 24, border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-sm)' },
  trendTitle: { fontSize: 15, fontWeight: 700, color: 'var(--sc-text-primary)', margin: '0 0 4px', letterSpacing: '-0.1px', fontFamily: "'Outfit', sans-serif" },
  trendSubtitle: { fontSize: 12, color: 'var(--sc-text-secondary)', margin: '0 0 16px' },
  analyticsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 8 },
  analyticCard: { background: 'var(--sc-bg-secondary)', borderRadius: 16, padding: 24, border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-sm)' },
  analyticTitle: { fontSize: 15, fontWeight: 700, color: 'var(--sc-text-primary)', margin: '0 0 16px', letterSpacing: '-0.1px', fontFamily: "'Outfit', sans-serif" },

  barChart: { display: 'flex', alignItems: 'flex-end', gap: 10, height: 240, padding: '0 4px' },
  barCol: { flex: 1, display: 'flex', flexDirection: 'column' as const, alignItems: 'center', gap: 7, cursor: 'default' },
  barValue: { fontSize: 12, fontWeight: 800, lineHeight: 1 },
  barTrack: { width: '100%', height: 170, background: 'var(--sc-bg-primary)', borderRadius: '10px 10px 0 0', display: 'flex', alignItems: 'flex-end', overflow: 'hidden', position: 'relative' as const, boxShadow: 'inset 0 3px 8px rgba(0,0,0,0.03), 0 2px 4px rgba(0,0,0,0.01)' },
  barFill: { width: '100%', borderRadius: '8px 8px 0 0', minHeight: 6, transformOrigin: 'bottom center' },
  barLabel: { fontSize: 11 },

  funnelList: { display: 'flex', flexDirection: 'column' as const, gap: 12, marginBottom: 18 },
  funnelRow: { display: 'flex', alignItems: 'center', gap: 10 },
  funnelLabel: { width: 90, fontSize: 12, fontWeight: 600, color: 'var(--sc-text-primary)' },
  funnelBarTrack: { flex: 1, height: 14, background: 'var(--sc-bg-primary)', borderRadius: 7, overflow: 'hidden' },
  funnelBarFill: { height: '100%', borderRadius: 7, transition: 'width 0.6s ease', minWidth: 4 },
  funnelCount: { width: 30, textAlign: 'right' as const, fontSize: 13, fontWeight: 700, color: 'var(--sc-text-primary)' },
  funnelSummary: { display: 'flex', gap: 24, paddingTop: 14, borderTop: '1px solid var(--sc-border)' },
  funnelStat: { display: 'flex', flexDirection: 'column' as const },
  funnelStatNum: { fontSize: 22, fontWeight: 800, color: 'var(--sc-text-primary)' },
  funnelStatLabel: { fontSize: 11, color: 'var(--sc-text-secondary)', fontWeight: 500 },

  serviceList: { display: 'flex', flexDirection: 'column' as const, gap: 10 },
  serviceRow: { display: 'flex', alignItems: 'center', gap: 10 },
  serviceLabel: { flex: 1.5, display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 600, color: 'var(--sc-text-primary)' },
  serviceDot: { width: 10, height: 10, borderRadius: '50%', flexShrink: 0 },
  serviceBarTrack: { flex: 2, height: 8, background: 'var(--sc-bg-primary)', borderRadius: 4, overflow: 'hidden' },
  serviceBarFill: { height: '100%', borderRadius: 4, transition: 'width 0.6s ease', minWidth: 4 },
  serviceStats: { width: 90, display: 'flex', gap: 8, justifyContent: 'flex-end' },
  serviceCount: { fontSize: 11, fontWeight: 600, color: 'var(--sc-text-secondary)' },
  serviceValue: { fontSize: 11, fontWeight: 700, color: 'var(--sc-brand-primary)' },

  metricsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 },
  metricItem: { display: 'flex', flexDirection: 'column' as const, alignItems: 'center', gap: 6, padding: 8 },
  metricCircle: { position: 'relative' as const, width: 72, height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  metricCircleText: { position: 'absolute' as const, fontSize: 16, fontWeight: 800, color: 'var(--sc-brand-primary)' },
  metricBigNum: { fontSize: 28, fontWeight: 800, lineHeight: 1 },
  metricLabel: { fontSize: 11, fontWeight: 500, color: 'var(--sc-text-secondary)', textAlign: 'center' as const },

  table: { background: 'var(--sc-bg-secondary)', borderRadius: 16, border: '1px solid var(--sc-border)', overflow: 'hidden', boxShadow: 'var(--sc-shadow-sm)' },
  tableHead: { display: 'flex', padding: '12px 20px', background: 'var(--sc-bg-primary)', borderBottom: '1px solid var(--sc-border)' },
  th: { flex: 1, fontSize: 11, fontWeight: 700, color: 'var(--sc-text-secondary)', textTransform: 'uppercase' as const, letterSpacing: 0.5 },
  tableRow: { display: 'flex', padding: '14px 20px', borderBottom: '1px solid var(--sc-divider)', textDecoration: 'none', cursor: 'pointer', transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)', alignItems: 'center' },
  td: { flex: 1, fontSize: 13.5, color: 'var(--sc-text-primary)' },
  badge: { padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, border: '1px solid' },

  emptyBox: { textAlign: 'center' as const, padding: 60, background: 'var(--sc-bg-secondary)', borderRadius: 16, border: '1px solid var(--sc-border)', color: 'var(--sc-text-secondary)', boxShadow: 'var(--sc-shadow-sm)' },
  spinner: { width: 32, height: 32, border: '3px solid var(--sc-border)', borderTopColor: 'var(--sc-brand-primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' },

  mobileContainer: { display: 'flex', flexDirection: 'column', minHeight: '100%', background: 'var(--sc-bg-primary)' },
  mobileHero: {
    padding: '12px 24px 28px 24px',
    background: 'var(--sc-bg-hero-gradient, #0A1C30)',
    color: '#FFFFFF',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
  },
  paypalCardLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: '#94A3B8',
    letterSpacing: '1.5px',
    marginBottom: 8,
  },
  paypalCardBal: {
    fontSize: 32,
    fontWeight: 800,
    margin: '0 0 6px 0',
    letterSpacing: '-0.5px',
    color: '#FFFFFF',
    fontFamily: "'Outfit', sans-serif",
  },
  paypalCardSub: {
    fontSize: 12,
    color: '#94A3B8',
    marginBottom: 16,
  },
  paypalCardBtn: {
    display: 'inline-flex',
    padding: '8px 20px',
    background: '#FFFFFF',
    color: '#0A1C30',
    borderRadius: 20,
    fontSize: 13.5,
    fontWeight: 700,
    textDecoration: 'none',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    transition: 'all 0.2s',
  },
  mobileSheet: {
    background: 'var(--sc-bg-primary)',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    padding: '24px 20px',
    flex: 1,
    boxShadow: '0 -8px 24px rgba(0,0,0,0.12)',
  },
  actionGridMobile: {
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    marginBottom: 20,
  },
  actionItemMobile: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: 6,
    textDecoration: 'none',
    width: 60,
  },
  actionCircleMobile: {
    width: 50,
    height: 50,
    borderRadius: 25,
    background: 'var(--sc-brand-gradient, #0C5ADB)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 10px rgba(12, 90, 219, 0.25)',
    transition: 'transform 0.15s ease',
  },
  actionLabelMobile: {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--sc-text-primary)',
  },
  sectionDivider: {
    height: 1,
    background: 'var(--sc-divider)',
    margin: '0 0 20px 0',
  },
  mobileSecTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: 'var(--sc-text-primary)',
    marginBottom: 12,
    fontFamily: "'Outfit', sans-serif",
  },
  avatarScroll: {
    display: 'flex',
    gap: 16,
    overflowX: 'auto' as const,
    paddingBottom: 8,
    scrollbarWidth: 'none' as const,
  },
  avatarCol: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: 6,
    cursor: 'pointer',
    minWidth: 64,
  },
  avatarCircle: {
    width: 48,
    height: 48,
    borderRadius: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: 'var(--sc-shadow-xs)',
  },
  avatarCircleText: {
    fontSize: 16,
    fontWeight: 700,
  },
  avatarName: {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--sc-text-secondary)',
    textAlign: 'center' as const,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: 60,
  },
  activityList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8,
  },
  activityItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 14px',
    background: 'var(--sc-bg-secondary)',
    borderRadius: 14,
    border: '1px solid var(--sc-border)',
    textDecoration: 'none',
    gap: 12,
  },
  activityIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    background: 'var(--sc-bg-primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  activityInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    minWidth: 0,
  },
  activityTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: 'var(--sc-text-primary)',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  activityDate: {
    fontSize: 11,
    color: 'var(--sc-text-secondary)',
    marginTop: 2,
  },
  activityValue: {
    fontSize: 14,
    fontWeight: 800,
  },
};
